import Repository from "./Repository";
import {
  EmploymentInfo,
  Member,
  MemberDetails,
  StrengthFinderDetails,
  EmployeeTitle,
  CheckIns,
  CheckinType,
} from "./types/Members";
const resource = "/members";

export default {
  getMembers() {
    return Repository.get<Array<Member>>(`${resource}`);
  },
  async getMemberProfilePicture(memberId: string): Promise<string | undefined> {
    return Repository.get<any>(`${resource}/${memberId}/photo`, {
      responseType: "blob",
    })
      .then((response: any) => {
        if (response.status === 204) {
          return undefined;
        } else {
          return URL.createObjectURL(response.data);
        }
      })
      .catch(error => {
        console.error("Member Profile Picture:", error);
        return undefined;
      });
  },
  updateMemberProfilePicture(memberId: string, photo: File) {
    const form = new FormData();
    form.append("memberProfilePhoto", photo);
    return Repository.patch<any>(`${resource}/${memberId}/photo`, form, {
      responseType: "blob",
    });
  },
  async getSpousePicture(memberId: string) {
    return Repository.get<any>(`${resource}/${memberId}/spouse/photo`, {
      responseType: "blob",
    }).then((response: any) => {
      if (response.status === 204) {
        return undefined;
      } else {
        return URL.createObjectURL(response.data);
      }
    });
  },
  updateSpousePicture(memberId: string, photo: File) {
    const form = new FormData();
    form.append("spouseProfilePhoto", photo);
    return Repository.patch<any>(`${resource}/${memberId}/spouse/photo`, form, {
      responseType: "blob",
    });
  },
  getMemberDetails(memberId: string) {
    return Repository.get<MemberDetails>(
      `${resource}/${memberId}?displayFields=all`
    );
  },
  updateMemberDetails(memberId: string, requestBody: Partial<MemberDetails>) {
    return Repository.patch<MemberDetails>(
      `${resource}/${memberId}`,
      requestBody
    );
  },
  updateEmployeeInfo(memberId: string, requestBody: EmploymentInfo) {
    const data = new FormData();
    for (const [key, val] of Object.entries(requestBody)) {
      //ignore blobs or undefined
      if (
        val === undefined ||
        (!(val instanceof File) && val instanceof Blob)
      ) {
        continue;
      }
      data.append(key, val);
    }
    return Repository.patch<MemberDetails>(
      `${resource}/employment/${memberId}`,
      data
    );
  },
  deleteIndividual(memberId: string, individualId: string) {
    return Repository.delete<any>(`${resource}/${memberId}/${individualId}`);
  },
  getStrengthFinderAttributes() {
    return Repository.get<StrengthFinderDetails[]>(
      `${resource}/strengthfinder/attributes`
    );
  },
  updateStrengthFinderAttributes(
    memberId: string,
    requestBody: Partial<EmploymentInfo>
  ) {
    return Repository.patch<any>(
      `${resource}/strengthsFinder/${memberId}`,
      requestBody
    );
  },
  getTeamLeaders() {
    return Repository.get<any>(`${resource}/teamleaders`);
  },
  getTitles() {
    return Repository.get<Array<EmployeeTitle>>("/employeeTitles");
  },
  getCheckIns(memberId: string) {
    return Repository.get<CheckIns>(`${resource}/${memberId}/checkins`);
  },
  deleteCheckIns(memberId: string, checkInId: string) {
    return Repository.delete<any>(
      `${resource}/${memberId}/checkins/${checkInId}`
    );
  },
  createCheckIn(
    memberId: string,
    checkInValues: Record<string, any>,
    date: Date,
    type: number,
    file?: File
  ) {
    const dateStr = date
      .toLocaleString("en-us", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/(\d+)\/(\d+)\/(\d+)/, "$3-$1-$2");

    if (type === CheckinType.MONTHLY_CHECKIN) {
      const data = new FormData();
      data.append("review", JSON.stringify(checkInValues));
      data.append("timePeriod", dateStr);
      data.append("checkinType", type.toString());
      if (file) data.append("checkins", file);
      return Repository.post<any>(`${resource}/${memberId}/checkins`, data);
    } else {
      const data = {
        review:
          type === CheckinType.MONTHLY_CHECKIN
            ? checkInValues
            : JSON.stringify(checkInValues),
        timePeriod: dateStr,
        checkinType: type,
      };
      return Repository.post<any>(`${resource}/${memberId}/checkins`, data);
    }
  },
  getCheckIn(memberId: string, checkInId: string) {
    return Repository.get<any>(`${resource}/${memberId}/checkins/${checkInId}`);
  },
  updateCheckIn(
    memberId: string,
    checkInId: string,
    checkInValues: Record<string, any>,
    date: Date,
    type: number,
    file?: File
  ) {
    const dateStr = date
      .toLocaleString("en-us", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/(\d+)\/(\d+)\/(\d+)/, "$3-$1-$2");

    if (type === CheckinType.MONTHLY_CHECKIN) {
      const data = new FormData();
      data.append("review", JSON.stringify(checkInValues));
      data.append("timePeriod", dateStr);
      data.append("checkinType", type.toString());
      if (file) data.append("checkins", file);
      return Repository.patch<any>(
        `${resource}/${memberId}/checkins/${checkInId}`,
        data
      );
    } else {
      const data = {
        review:
          type === CheckinType.MONTHLY_CHECKIN
            ? checkInValues
            : JSON.stringify(checkInValues),
        timePeriod: dateStr,
        checkinType: type,
      };
      return Repository.patch<any>(
        `${resource}/${memberId}/checkins/${checkInId}`,
        data
      );
    }
  },
  async getCheckInFile(memberId: string, checkInId: string) {
    return Repository.get<Blob>(
      `${resource}/${memberId}/checkins/${checkInId}/download`,
      {
        responseType: "blob",
      }
    ).then(res => {
      return res.data;
    });
  },
  async getEmploymentDocument(
    memberId: string,
    documentType: string,
    fileName: string
  ) {
    const params = new URLSearchParams();
    params.set("fileName", fileName);
    params.set("documentType", documentType);
    return Repository.get<Blob>(
      `${resource}/${memberId}/employmentDocument?${params.toString()}`,
      {
        responseType: "blob",
      }
    ).then(res => {
      return res.data;
    });
  },
  getMyTeam() {
    return Repository.get<any>(`${resource}/teamleaders`);
  },
};
